import React, { useState, useEffect } from 'react';
import './App.css';
import { auth, db } from '../firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { supabase } from './supabaseClient';
import defaultUser from './user-default.png';
import AvailabilityPicker from './AvailabilityPicker';

function MentorDashboard() {
  const [userId, setUserId] = useState(null);
  const [fullNameInput, setFullNameInput] = useState('');
  const [bioInput, setBioInput] = useState('');
  const [profileUrl, setProfileUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [availability, setAvailability] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  const displayMessage = (msg, type) => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 5000);
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        setUserId(user.uid);
        setAvailability(true);
        const docRef = doc(db, 'users', user.uid);
        const userSnap = await getDoc(docRef);
        if (userSnap.exists()) {
          const data = userSnap.data();
          setFullNameInput(data.fullName || '');
          setBioInput(data.bio || '');
        }
        const { data } = supabase.storage
          .from('profile-pictures')
          .getPublicUrl(`profile-pictures/${user.uid}/profile.jpg`);
        setProfileUrl(data.publicUrl);
      } else {
        setUserId(null);
        setAvailability(false);
      }
    });
    return () => unsubscribe();
  }, []);

  const uploadProfilePic = async (e) => {
    if (!userId) return;
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const filePath = `profile-pictures/${userId}/profile.jpg`;
      const { error } = await supabase.storage
        .from('profile-pictures')
        .upload(filePath, file, { upsert: true });
      if (error) throw error;
      const { data } = supabase.storage.from('profile-pictures').getPublicUrl(filePath);
      setProfileUrl(data.publicUrl);
      displayMessage('Profile picture uploaded!', 'success');
    } catch (err) {
      console.error(err);
      displayMessage('Failed to upload profile picture.', 'error');
    } finally {
      setUploading(false);
    }
  };

  const saveFullName = async () => {
    if (!userId) return;
    if (!fullNameInput.trim()) {
      displayMessage('Full name cannot be empty.', 'error');
      return;
    }
    try {
      await updateDoc(doc(db, 'users', userId), {
        fullName: fullNameInput.trim(),
      });
      displayMessage('Full name saved successfully!', 'success');
    } catch (err) {
      console.error(err);
      displayMessage('Failed to save full name.', 'error');
    }
  };

  const saveBio = async () => {
    if (!userId) return;
    try {
      await updateDoc(doc(db, 'users', userId), {
        bio: bioInput.trim(),
      });
      displayMessage('Bio updated successfully!', 'success');
    } catch (err) {
      console.error(err);
      displayMessage('Failed to update bio.', 'error');
    }
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.header}>Mentor Dashboard</h2>

      <div style={styles.mainGrid}>
        {/* Left side: Profile */}
        <div style={styles.card}>
          <div style={styles.profileSection}>
            <img
              src={profileUrl || defaultUser}
              alt="Profile"
              onError={(e) => (e.target.src = defaultUser)}
              style={styles.profileImage}
            />
            <p style={styles.statusText}>
              Status:{' '}
              <span style={{ color: availability ? 'green' : 'red' }}>
                {availability ? 'Online' : 'Offline'}
              </span>
            </p>
            <input
              type="file"
              accept="image/*"
              onChange={uploadProfilePic}
              disabled={uploading}
              style={styles.fileInput}
            />
          </div>

          <div style={styles.infoSection}>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Full Name</label>
              <input
                type="text"
                value={fullNameInput}
                onChange={(e) => setFullNameInput(e.target.value)}
                placeholder="Enter your full name"
                style={styles.input}
              />
              <button onClick={saveFullName} disabled={uploading} style={styles.button}>
                Save Name
              </button>
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>Bio</label>
              <textarea
                value={bioInput}
                onChange={(e) => setBioInput(e.target.value)}
                placeholder="Tell us about yourself"
                rows={5}
                style={styles.textarea}
              />
              <button onClick={saveBio} disabled={uploading} style={styles.button}>
                Save Bio
              </button>
            </div>
          </div>
        </div>

        {/* Right side: Availability */}
        <div style={styles.availabilityCard}>
          <AvailabilityPicker />
        </div>
      </div>

      {message && (
        <div
          style={{
            ...styles.message,
            backgroundColor: messageType === 'error' ? '#ffe2e2' : '#e0ffe3',
            color: messageType === 'error' ? '#900' : '#155724',
          }}
        >
          {message}
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    padding: 30,
    fontFamily: 'Arial, sans-serif',
    backgroundColor: '#f8f9fa',
    minHeight: '100vh',
  },
  header: {
    fontSize: '28px',
    marginBottom: 20,
    color: '#343a40',
  },
  mainGrid: {
    display: 'flex',
    gap: 30,
    alignItems: 'flex-start',
    flexWrap: 'wrap',
  },
  card: {
    flex: 1,
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    boxShadow: '0 0 10px rgba(0,0,0,0.1)',
    minWidth: 350,
  },
  availabilityCard: {
    flex: 1,
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    boxShadow: '0 0 10px rgba(0,0,0,0.1)',
    minWidth: 350,
  },
  profileSection: {
    textAlign: 'center',
    marginBottom: 20,
  },
  profileImage: {
    width: 160,
    height: 160,
    borderRadius: '50%',
    border: '4px solid #ddd',
    objectFit: 'cover',
  },
  statusText: {
    marginTop: 10,
    fontSize: 16,
  },
  fileInput: {
    marginTop: 10,
    cursor: 'pointer',
  },
  infoSection: {
    marginTop: 10,
  },
  fieldGroup: {
    marginBottom: 20,
  },
  label: {
    display: 'block',
    marginBottom: 6,
    fontWeight: 'bold',
    color: '#333',
  },
  input: {
    width: '100%',
    padding: 10,
    fontSize: 16,
    borderRadius: 6,
    border: '1px solid #ccc',
    marginBottom: 10,
  },
  textarea: {
    width: '100%',
    padding: 10,
    fontSize: 16,
    borderRadius: 6,
    border: '1px solid #ccc',
    resize: 'vertical',
    marginBottom: 10,
  },
  button: {
    padding: '10px 16px',
    backgroundColor: '#007bff',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
  },
  message: {
    marginTop: 20,
    padding: 12,
    borderRadius: 8,
    fontWeight: 500,
    maxWidth: 500,
  },
};

export default MentorDashboard;
